import { isEmpty } from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector } from '@reduxjs/toolkit';

import { LanguageStore } from 'app/lang';
import {
  getSportAdditionalLineIdConf,
  getSportLinesConf,
  getSportLinesStakeTypesConf,
  getSportMainLineIdConf,
  getStakeRendererConf, isFilteredLineStake,
} from 'app/lib/matchGridStakes';
import { parseQuery } from 'app/lib/url';
import { STICKY_HEADER_MARGIN_RIGHT } from 'app/modules/Live/constants';
import {
  getTournamentsBySportIdsSortingByPriority,
} from 'app/redux/betting-presentation/grid/utils/get-tournaments-by-sport-ids-sorting-by-priority';

import { selectAllSportsGrid } from './allSportsGrid';
import selectFavoritesGrid from './favoritesGrid';
import selectFavoritesMatchIds from './favoritesMatchIds';
import selectGridHeader from './gridHeader';
import selectGridSubheaderBreadcrumb from './gridSubheaderBreadCrumb';
import selectMatchInnerList from './matchInnerLineList';
import selectMatchLineScores from './matchLineScores';
import selectMatchesStakes from './matchStakes';
import selectPrematchTournamentGrid from './prematchTournamentGrid';
import selectPrematchUnionGrid from './prematchUnionGrid';
import selectResponsiveProps from './responsiveLayout';
import { selectTournamentsGroupedByPromotionLevel } from './selectTournamentsGroupedByPromotionLevel';
import selectStageGrid from './stageGrid';
import selectStakeSlots from './stakeSlots';
import selectStakeSlotWithMainStakes from './stakeSlotWithMainStakes';
import selectTournamentGridSubheaderName from './tournamentGridSubheaderName';
import selectTournamentMatchIds from './tournamentMatchIds';
import selectVisibleLineStakeIds from './visibleLineStakeIds';
import selectVisibleStakeTypeGroupIds from './visibleStakeTypeGroupIds';
import { selectGroupedTournamentsBySportId } from './visibleTournamentIds';

const currentFilter = createSelector(
  (state) => state.router.location.search,
  (search) => {
    const initFilter = {
      sportId: null,
      tournamentUnionId: null,
      tournamentId: null,
      tournamentStageId: null,
      matchId: null,
    };

    if (!search) {
      return initFilter;
    }

    const query = parseQuery(search);

    return Object.keys(initFilter).reduce((map, key) => {
      if (query[key]) {
        map[key] = parseInt(query[key]);
      }

      return map;
    }, initFilter);
  },
);

/**
 * @param {string} namespace
 * @param {object} entities
 * @param {?string[]} path - reducer nodes path
 * @return {object}
 */
export default (
  namespace,
  {
    sports,
    sportIds,
    tournaments,
    tournamentUnions,
    tournamentStages,
    matches,
    isMatchStarted,
    favoriteMatchIds,
    favoriteTournamentIds,
    getUnionsFromSportsSelector,
    getTournamentsFromSportsSelector,
    getTournamentsFromUnionsSelector,
    getStagesFromTournamentsSelector,
  },
  { path },
) => {
  /**
   * Get grid's stake type group ids.
   * Every sport has a personal list of `stakeTypeGroup` ids.
   *
   * @param {object} state
   * @param {number} sportId
   * @return {Immutable.List<number>}
   **/
  const stakeTypeGroupBySportId = (state, { sportId }) => {
    return state[namespace].getIn([...path, 'stakeTypeGroupBySportId', sportId]);
  };


  /**
   * Get match related sport id.
   *
   * @param {object} state
   * @param {number} matchId
   * @return {number|null}
   */
  const matchSportId = (state, { matchId }) => {
    const match = matches(state).get(matchId);

    return match ? match.sportId : null;
  };

  const responsiveProps = selectResponsiveProps();

  const createVisibleStakeTypeGroupIdsSelector = selectVisibleStakeTypeGroupIds(
    matchSportId,
    stakeTypeGroupBySportId,
  );

  const transparentSpaceWidthSelector = createCachedSelector(
    (state, sportId) => createVisibleStakeTypeGroupIdsSelector()(state, { sportId }),
    (visibleStakeTypeGroup) =>
      getMainCellWidth(visibleStakeTypeGroup && visibleStakeTypeGroup.size)
        + STICKY_HEADER_MARGIN_RIGHT,
  )((state, sportId) => sportId);

  const matchStakesSelector = selectMatchesStakes(matches);

  /**
     * Get main-line `id` for sport.
     *
     * @param {object} state
     * @param {number} matchId
     * @return {number | null}
     */
  const mainLineId = (state, { matchId }) => {
    return getSportMainLineIdConf(matchSportId(state, { matchId }));
  };

  /**
   * Get match-line-ids
   *
   * @param {object} state
   * @param {number} matchId
   * @return {Record<number, Immutable.List<StakeEntity>>}
   */

  const matchStakesInLineSelector = createCachedSelector(
    [
      (state, matchId) => matchSportId(state, { matchId }),
      matchStakesSelector,
      (state, matchId) => getSportLinesStakeTypesConf(matchSportId(state, { matchId })),
    ],
    (sportId, stakes, linesStakeTypesConf) => {
      const matchStakesInLine = stakes.reduce((stakesInLine, stake) => {
        const matchLines = linesStakeTypesConf.filter((lineStakeTypesConf) => {
          return (
            lineStakeTypesConf?.stakeTypes?.find((stakeTypeConf) => {
              const stakeType = stakeTypeConf.stakeType || stakeTypeConf;
              const filter = stakeTypeConf.filter;

              const isCorrectStakeType = stakeType === stake.type;

              return isEmpty(filter)
                ? isCorrectStakeType
                : isCorrectStakeType && isFilteredLineStake(filter, stake.params);
            })
          );
        });

        if (matchLines.length === 0) {
          const lineId = getSportAdditionalLineIdConf(sportId);
          lineId in stakesInLine
            ? stakesInLine[lineId].push(stake)
            : Object.assign(stakesInLine, { [lineId]: [stake] });
        }
        else {
          matchLines.forEach((line) => {
            line.lineId in stakesInLine
              ? stakesInLine[line.lineId].push(stake)
              : Object.assign(stakesInLine, { [line.lineId]: [stake] });
          });
        }

        return stakesInLine;
      }, {});

      return matchStakesInLine;
    },
  )((_state, matchId) => matchId);

  /**
   * Get match-lines
   *
   * @param {object} state
   * @param {number} matchId
   * @return LineEntry[]
   */

  const matchLinesSelector = createCachedSelector(
    [
      (state, matchId) => matchSportId(state, { matchId }),
      (state, matchId) => matchStakesInLineSelector(state, matchId),
      (state, matchId) => getSportLinesConf(matchSportId(state, { matchId })),
    ],
    (matchSportId, matchStakesInLine, sportLineConf, additional) => {
      return sportLineConf.filter((lineConf) => lineConf.id in matchStakesInLine);
    },
  )((_state, matchId) => matchId);

  /**
   * Get match-lines
   *
   * @param {object} state
   * @param {number} matchId
   * @return lineEntryOptions[]
   */

  const lineOptionsWithNumberOfBetsSelector = createCachedSelector(
    [
      matchStakesInLineSelector,
      matchLinesSelector,
    ],
    (matchStakesInLine, matchLines) => {
      const lang = LanguageStore.getLang();
      return matchLines.reduce((acc, curr) => {
        const stakes = matchStakesInLine[curr.id];
        if (stakes) {
          const betsCount = stakes.reduce((acc, curr) => {
            acc += curr.bets.size;

            return acc;
          }, 0);

          acc.push({ id: curr.id, value: `${curr.names[lang]} (${betsCount})` });
        }

        return acc;
      }, []);
    },
  )((_state, matchId) => matchId);

  const visibleLineStakeIdsSelector = selectVisibleLineStakeIds(
    namespace,
    path,
    createVisibleStakeTypeGroupIdsSelector,
  );

  const matchInnerLineListSelector = selectMatchInnerList(matchSportId, matchStakesSelector);

  const stakeSlotsSelector = selectStakeSlots(
    matchStakesSelector,
    visibleLineStakeIdsSelector,
    matchInnerLineListSelector,
    matchSportId,
  );

  const stakeSlotsWithMainStakesSelector = selectStakeSlotWithMainStakes(
    matchSportId,
    matchStakesSelector,
    matchInnerLineListSelector,
  );

  const stakeSlotsNumberOfBetSelector = createCachedSelector(
    stakeSlotsWithMainStakesSelector,
    (stakesSlots) => {
      return stakesSlots?.reduce((size, slot) => {
        slot.stakes.forEach((stake) => {
          size += stake.bets.size;
        });
        return size;
      }, 0);
    },
  )((state, lineId, matchId) => `${matchId}.${lineId}`);

  /**
     * Create the Immutable.List selector that is built depending on the given `matchId`.
     *
     * The resulting list contain "not-null" entries or entries with "not-null" stakeSlots.
     *
     * @param {object} state
     * @param {number} matchId
     * @return Immutable.List<RecordOf<InnerLineEntry>>}
     */
  const innerLineListWithNoEmptyLinesSelector = (state, matchId) => {
    return matchInnerLineListSelector(state, matchId).filter((innerLine) => {
      const lineId = innerLine.id;
      const stakeIds = lineId && visibleLineStakeIdsSelector(state, lineId, matchId);

      return stakeSlotsSelector(state, lineId, matchId).size
          || stakeIds?.some((stakeId) => Boolean(stakeId));
    });
  };

  const mainCellWidthSelector = createCachedSelector(
    visibleLineStakeIdsSelector,
    (visibleLineStakeIds) =>
      getMainCellWidth(visibleLineStakeIds && visibleLineStakeIds.size),
  )((state, lineId, matchId) => `${matchId}.${lineId}`);

  /**
     * Get grid stake layout (2 for `two-way`, 3 for `three-way`).
     *
     * @param {object} state
     * @param {number} stakeId
     * @param {number} matchId
     * @return {GRID_STAKE_LAYOUT}
     */
  const stakeLayout = (state, { stakeId, matchId }) => {
    const match = matches(state).get(matchId);

    if (match?.stakes) {
      const stake = match.stakes.get(stakeId);

      if (stake?.type !== undefined) {
        return getStakeRendererConf(stake.type);
      }
    }

    return null;
  };


  const favoriteMatchIdsWithoutTournamentsSelector = createSelector(
    matches,
    favoriteMatchIds,
    (
      matchesById,
      favoritesMatchIds,
    ) => {
      return favoritesMatchIds.filter((id) => matchesById.has(id));
    },
  );


  const favoriteTournamentIdsSelector = createSelector(
    tournaments,
    favoriteTournamentIds,
    (
      tournamentsById,
      favoriteTournamentIds,
    ) => {
      return favoriteTournamentIds.filter((id) => tournamentsById.has(id));
    },
  );


  const favoritesMatchIdsSelector = selectFavoritesMatchIds(
    tournaments,
    tournamentStages,
    matches,
    favoriteMatchIds,
    favoriteTournamentIds,
    currentFilter,
  );

  /**
     * Get favorites matches number
     *
     * @return {number}
     */
  const favoritesMatchesCounter = (state) => favoritesMatchIdsSelector(state).size;


  const gridSubheaderBreadcrumbSelector = selectGridSubheaderBreadcrumb(
    matches,
    sports,
    tournamentStages,
    tournaments,
    tournamentUnions,
  );

  const tournamentGridSubheaderName = selectTournamentGridSubheaderName(tournaments);

  const createGridHeaderSelector = selectGridHeader(
    tournaments,
    tournamentUnions,
    sports,
  );

  const stageGridSelector = selectStageGrid(
    matches,
    getStagesFromTournamentsSelector,
  );

  const allStageGridSelector = selectStageGrid(
    matches,
    getUnionsFromSportsSelector,
    tournaments,
  );

  const prematchUnionGridSelector = selectPrematchUnionGrid(
    matches,
    getUnionsFromSportsSelector,
    tournaments,
  );

  const tournamentMatchIdsSelector = selectTournamentMatchIds(
    matches,
    getStagesFromTournamentsSelector,
  );

  const allSportsGridSelector = selectAllSportsGrid(
    sports,
    tournamentUnions,
    tournaments,
    matches,
  );

  const tournamentsGroupedByPromotionLevelSelector = selectTournamentsGroupedByPromotionLevel(
    allSportsGridSelector,
  );

  const tournamentsGroupedByPromotionLevelBySportIdsSortingByPrioritySelector = createSelector(
    tournamentsGroupedByPromotionLevelSelector,
    getTournamentsBySportIdsSortingByPriority,
  );

  const groupedTournamentsBySportIdSelector = selectGroupedTournamentsBySportId(
    tournamentsGroupedByPromotionLevelSelector,
  );

  const prematchTournamentGridSelector = selectPrematchTournamentGrid(
    matches,
    getTournamentsFromUnionsSelector,
  );

  const createFavoritesGridSelector = () => selectFavoritesGrid(
    tournaments,
    matches,
    favoritesMatchIdsSelector,
  );

  const {
    innerLinesScores,
    matchMainScoresSelector,
  } = selectMatchLineScores(matchSportId, matches, isMatchStarted);

  return {
    sportIds,
    mainLineId,
    matchSportId,
    stakeLayout,
    responsiveProps,
    gridSubheaderBreadcrumbSelector,
    stakeTypeGroupBySportId,
    innerLinesScores,
    matchMainScoresSelector,
    favoritesMatchIdsSelector,
    favoriteTournamentIds,
    favoriteMatchIds,
    favoritesMatchesCounter,
    matchStakesSelector,
    stakeSlotsSelector,
    stakeSlotsWithMainStakesSelector,
    stakeSlotsNumberOfBetSelector,
    createGridHeaderSelector,
    allSportsGridSelector,
    tournamentsGroupedByPromotionLevelSelector,
    tournamentsGroupedByPromotionLevelBySportIdsSortingByPrioritySelector,
    prematchUnionGridSelector,
    prematchTournamentGridSelector,
    stageGridSelector,
    allStageGridSelector,
    groupedTournamentsBySportIdSelector,
    tournamentMatchIdsSelector,
    createFavoritesGridSelector,
    matchInnerLineListSelector,
    visibleLineStakeIdsSelector,
    innerLineListWithNoEmptyLinesSelector,
    lineOptionsWithNumberOfBetsSelector,
    createVisibleStakeTypeGroupIdsSelector,
    transparentSpaceWidthSelector,
    mainCellWidthSelector,
    favoriteMatchIdsWithoutTournamentsSelector,
    favoriteTournamentIdsSelector,
    tournamentGridSubheaderName,
  };
};

/**
 * Returns `true` if a match with the given `matchId` have been fully loaded.
 *
 * @param {object} state
 * @param {number} matchId
 * @param {string} namespace
 * @return {boolean}
 */
export const hasFullMatchContent = (state, matchId, namespace) => {
  return state[namespace].get('fullyLoadedMatches').has(matchId);
};

// ---

export const MAIN_CELL_STAKE_WIDTH = 134;
export const MAIN_CELL_PADDING = 12;

/**
 * @param {number} visibileStakeCount
 */
function getMainCellWidth(visibileStakeCount) {
  return MAIN_CELL_STAKE_WIDTH * (visibileStakeCount || 0) + MAIN_CELL_PADDING;
}

